import React, { Suspense } from 'react'
import { Redirect, Route, RouteProps, Switch } from 'react-router'
import { BrowserRouter as Router } from 'react-router-dom'
import { FillLoader, AdminSideBar } from '../components'
import PageNotFound from '../containers/PageNotFound'
import { useAuthContext } from '../context/AuthProvider'
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from './routes'
import { theme } from '../theme'
import { uniqBy } from 'lodash'
import { VscHome, VscSettings } from 'react-icons/vsc'
import { FaUsers } from 'react-icons/fa'

const ICON_COLOR = theme.colors.gray[500]
const ICON_SIZE = 16

interface RouteType extends RouteProps {
  component: any
}

export type NavItem = {
  to: string
  title: string
  icon: React.ReactNode
  subMenu?: Array<{ to: string; title: string }>
}
const NAV_ITEMS: NavItem[] = [
  {
    to: `/auth/dashboard/agent/dashboard`,
    title: 'Properties',
    icon: <VscHome size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
  }
]
const SECONDARY_NAV_ITEMS: NavItem[] = []

const PrivateRoute = ({ component: Component, ...rest }: RouteType) => {
  const { isAuthenticating, isAuthenticated } = useAuthContext()

  if (isAuthenticating) {
    return <FillLoader />
  }
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          <Suspense fallback={<FillLoader color="black" />}>
            <Component {...rest} />
          </Suspense>
        ) : (
          <Redirect to={{ pathname: '/', state: { from: props.location } }} />
        )
      }
    />
  )
}

const PublicRoute = ({ component: Component, ...rest }: RouteType) => {
  const { isAuthenticated } = useAuthContext()

  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          <Redirect
            to={{
              pathname: '/auth',
              state: { from: props.location }
            }}
          />
        ) : (
          <Component />
        )
      }
    />
  )
}

const Navigation = (): JSX.Element => {
  const { isAdmin } = useAuthContext()
  const [navItems, setNavItems] = React.useState<NavItem[]>(NAV_ITEMS)

  React.useEffect(() => {
    if (isAdmin) {
      setNavItems((prev) => {
        return [
          ...prev,
          {
            to: '/auth/dashboard/agent/team-management',
            title: 'Team Management',
            icon: <FaUsers size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
          },
          {
            to: '/auth/dashboard/settings/agency-info',
            title: 'Settings',
            icon: <VscSettings size={ICON_SIZE} color={ICON_COLOR} className="sidebar-menu-icon" />
          }
        ]
      })
    }
  }, [isAdmin])

  return (
    <Router>
      <Suspense fallback={<FillLoader />}>
        <Switch>
          {PUBLIC_ROUTES.map((route) => {
            return <PublicRoute key={route.path} {...route} />
          })}
          <Route
            path="/auth"
            render={() => {
              return (
                <AdminSideBar
                  bg="gray.50"
                  color={ICON_COLOR}
                  navItems={uniqBy(navItems, 'to')}
                  hoverColor={theme.colors.gray[200]}
                  accentColor={theme.colors.brand[400]}
                  secondaryNavItems={SECONDARY_NAV_ITEMS}
                  tooltipBg={theme.colors.brand[400]}
                  tooltipColor={theme.colors.gray[100]}
                >
                  {PRIVATE_ROUTES.map((route) => {
                    return <PrivateRoute key={route.path} {...route} />
                  })}
                </AdminSideBar>
              )
            }}
          />

          <Route component={PageNotFound} />
        </Switch>
      </Suspense>
    </Router>
  )
}

export default Navigation
